import { HICLoading } from 'hic-components'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomerDetailFragment, CustomerType, useCustomerQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { FormLayoutAppearance } from 'src/app/layout/form-layout/form-layout.types'
import NotFoundPage from '../../404.page'
import useCustomerApi, { CustomerResult } from './customer-api.hook'
import CustomerForm from './form/customer.form'
import { CustomerFormData, FocusedField } from './form/customer.form.types'

export type CustomerDetailProps =
  | {
      customerId: string
      appearance: FormLayoutAppearance
      onUpserted: (customer: CustomerDetailFragment) => void
      onCancel: () => void
      focusedField?: FocusedField
      guestOfReservedResourceId?: string
    }
  | {
      type: CustomerType
      appearance: FormLayoutAppearance
      onUpserted: (customer: CustomerDetailFragment) => void
      onCancel: () => void
      focusedField?: FocusedField
    }

export default function CustomerDetail(props: CustomerDetailProps) {
  if ('customerId' in props) {
    return (
      <FetchCustomer
        customerId={props.customerId}
        children={(customer) => (
          <InternalCustomerDetail //
            type={customer.type}
            initialCustomer={customer}
            appearance={props.appearance}
            onCancel={props.onCancel}
            onUpserted={props.onUpserted}
            focusedField={props.focusedField}
            guestOfReservedResourceId={props.guestOfReservedResourceId}
          />
        )}
      />
    )
  } else {
    return (
      <InternalCustomerDetail //
        type={props.type}
        appearance={props.appearance}
        onCancel={props.onCancel}
        onUpserted={props.onUpserted}
        focusedField={props.focusedField}
        initialCustomer={null}
      />
    )
  }
}

type FetchCustomerProps = {
  customerId: string
  children: (customer: CustomerDetailFragment) => React.ReactNode
  reservedResourceReferenceId?: string
}
function FetchCustomer({ customerId, children }: FetchCustomerProps) {
  const tenant = useTenant()
  const { data, loading } = useCustomerQuery({ variables: { tenantId: tenant.id, customerId } })
  const customer = useMemo(() => data?.customers?.[0], [data])
  if (loading) {
    return <HICLoading />
  }
  if (customer) {
    return <>{children(customer)}</>
  }
  return <NotFoundPage />
}

type InternalCustomerDetailProps = {
  type: CustomerType
  initialCustomer: CustomerDetailFragment | null
  appearance: FormLayoutAppearance
  onUpserted: (customer: CustomerDetailFragment) => void
  onCancel: () => void
  focusedField?: FocusedField
  guestOfReservedResourceId?: string
}

function InternalCustomerDetail({ type, initialCustomer, appearance, onUpserted, onCancel, focusedField, guestOfReservedResourceId }: InternalCustomerDetailProps) {
  const { t } = useTranslation()
  const showErrorSnackBar = useErrorSnackBar()
  const [loading, setLoading] = useState(false)
  const { create, update } = useCustomerApi()
  const onSubmit = useCallback(
    async (data: CustomerFormData) => {
      setLoading(true)
      try {
        let result: CustomerResult
        if (initialCustomer) {
          result = await update(initialCustomer.id, data, guestOfReservedResourceId)
        } else {
          result = await create(data)
        }
        if (result.type === 'SUCCESS') {
          setLoading(false)
          onUpserted(result.customer)
        } else {
          setLoading(false)
          showErrorSnackBar(result.errors || t('commons.generic_error'))
        }
      } catch (e) {
        showErrorSnackBar(e || t('commons.generic_error'))
        setLoading(false)
      }
    },
    [update, create, initialCustomer],
  )

  const title = useMemo(() => {
    if (initialCustomer?.type === CustomerType.PERSON) {
      return t('pms.common.customer.person_detail_title')
    }
    if (initialCustomer?.type === CustomerType.COMPANY) {
      return t('pms.common.customer.company_detail_title')
    }
    if (type === CustomerType.PERSON) {
      return t('pms.common.customer.person_detail_title')
    }
    if (type === CustomerType.COMPANY) {
      return t('pms.common.customer.company_detail_title')
    }
    return ''
  }, [type, initialCustomer])

  return (
    <CustomerForm //
      customerType={type}
      onSubmit={onSubmit}
      loading={loading}
      title={title}
      appearance={appearance}
      onCancel={onCancel}
      customer={initialCustomer}
      focusedField={focusedField}
    />
  )
}
